import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import GradientCircularProgress from '../../../../components/common/GradientCircularProgress';
import { useThemeContext } from '../../../../contexts/BaseThemeContextProvider';
import useBackendApi from '../../../../hooks/useBackendApi';
import { RequestOptions } from '../../../../types/httpRequests';
import { AccountType, AccountStaffMember } from '../../../../types/account';
import BorderedButton from '../../../../components/common/buttons/BorderedButton';
import { useGhostButtonProps } from '../../../../hooks/useGhostButtonProps';
import AddStaffModal from '../../components/AddStaffModal';
import type { SelectChangeEvent } from '@mui/material/Select';
import { StaffSearchResult } from '../../../../types/api/organizationStaff';
import StaffTable from '../../components/StaffTable';
import GhostButton from '../../../../components/common/buttons/GhostButton';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type RouteParams = {
  org_path_name: string;
  acc_path_name: string;
};

type ActionMessage = {
  type: 'success' | 'error';
  text: string;
};

export type AccountStaffResponse = {
  message?: string;
  status_code?: number;
  detail?: string;
  account?: AccountType;
  staff?: AccountStaffMember[];
  user_org_roles?: string[];
  user_acc_roles?: string[];
};

type ApiFetchRequest = (options: RequestOptions) => Promise<{
  status: number;
  body?: unknown;
  isError?: boolean;
} | null>;

export type FetchAccountStaffResult = {
  status: number;
  body?: AccountStaffResponse;
  isError: boolean;
  unauthorized: boolean;
  notFound: boolean;
};

export const fetchAccountStaff = async ({
  apiFetchRequest,
  backendURL,
  orgPathName,
  accPathName,
}: {
  apiFetchRequest: ApiFetchRequest;
  backendURL: string;
  orgPathName: string;
  accPathName: string;
}): Promise<FetchAccountStaffResult | null> => {
  const opts: RequestOptions = {
    endpoint: `${backendURL}/o/${encodeURIComponent(orgPathName)}/a/${encodeURIComponent(accPathName)}/staff/`,
    method: 'GET',
  };

  const res = await apiFetchRequest(opts);
  if (!res) {
    return null;
  }

  const body = res.body as AccountStaffResponse | undefined;
  const unauthorized =
    res.status === 401 ||
    body?.status_code === 401 ||
    body?.message === 'UNAUTHENTICATED' ||
    body?.message === 'NO_ACCESS';
  const notFound = res.status === 404 || body?.status_code === 404 || body?.message === 'NOT_FOUND';

  return {
    status: res.status,
    body,
    isError: Boolean(res.isError),
    unauthorized,
    notFound,
  };
};

type AccountStaffRolesResponse = {
  message?: string;
  status_code?: number;
  detail?: string;
  roles?: string[];
};

export default function AccountStaff(): JSX.Element {
  const { org_path_name, acc_path_name } = useParams<RouteParams>();
  const navigate = useNavigate();
  const { theme } = useThemeContext();
  const { apiFetchRequest, isLoading } = useBackendApi();

  const addStaffButtonProps = useGhostButtonProps({
    default: '/icons/user_gray550.svg',
    hover: '/icons/user_blue.svg',
  });

  const [account, setAccount] = useState<AccountType | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [staff, setStaff] = useState<AccountStaffMember[]>([]);
  const [userOrgRoles, setUserOrgRoles] = useState<string[]>([]);
  const [userAccRoles, setUserAccRoles] = useState<string[]>([]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [roles, setRoles] = useState<string[]>([]);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [isRolesLoading, setIsRolesLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedCandidate, setSelectedCandidate] = useState<StaffSearchResult | null>(null);
  const [isAddingStaff, setIsAddingStaff] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [dialogMessage, setDialogMessage] = useState<ActionMessage | null>(null);

  const [inputValue, setInputValue] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [searchResults, setSearchResults] = useState<StaffSearchResult[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const [actionMessage, setActionMessage] = useState<ActionMessage | null>(null);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);

  const isOrgAdmin = userOrgRoles.some((r) => r?.toLowerCase() === 'admin');
  const isAccAdmin = userAccRoles.some((r) => r?.toLowerCase() === 'admin');
  const canManageStaff = isOrgAdmin || isAccAdmin;

  const fail = useCallback(() => setErrorText('Something went wrong. Please try again.'), []);

  const handleCommonAuthNav = useCallback(
    (resStatus?: number, body?: { status_code?: number; message?: string }) => {
      if (
        resStatus === 401 ||
        body?.status_code === 401 ||
        body?.message === 'UNAUTHENTICATED' ||
        body?.message === 'NO_ACCESS'
      ) {
        navigate('/401.html');
        return true;
      }
      if (resStatus === 404 || body?.status_code === 404 || body?.message === 'NOT_FOUND') {
        navigate('/404.html');
        return true;
      }
      return false;
    },
    [navigate],
  );

  const fetchAccountStaffData = useCallback(async () => {
    if (!org_path_name || !acc_path_name) return;

    try {
      const res = await fetchAccountStaff({
        apiFetchRequest,
        backendURL,
        orgPathName: org_path_name,
        accPathName: acc_path_name,
      });
      if (!res) {
        fail();
        return;
      }

      if (handleCommonAuthNav(res.status, res.body)) return;

      if (res.isError || res.status !== 200 || res.body?.message !== 'SUCCESS' || !res.body?.account) {
        fail();
        return;
      }

      setAccount(res.body.account);
      setStaff(Array.isArray(res.body.staff) ? res.body.staff : []);
      setUserOrgRoles(Array.isArray(res.body.user_org_roles) ? res.body.user_org_roles : []);
      setUserAccRoles(Array.isArray(res.body.user_acc_roles) ? res.body.user_acc_roles : []);
      setErrorText(null);
    } catch {
      fail();
    }
  }, [acc_path_name, apiFetchRequest, fail, handleCommonAuthNav, org_path_name]);

  const fetchAssignableRoles = useCallback(async () => {
    if (!org_path_name || !acc_path_name) return;

    setIsRolesLoading(true);
    setRolesError(null);

    try {
      const options: RequestOptions = {
        endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(acc_path_name)}/staff/roles`,
        method: 'GET',
      };

      const response = await apiFetchRequest(options);
      if (!response) {
        setRolesError('Unable to load account roles right now.');
        setRoles([]);
        setSelectedRole('');
        return;
      }

      const body = response.body as AccountStaffRolesResponse;
      if (handleCommonAuthNav(response.status, body)) return;

      if (response.isError || body?.message !== 'SUCCESS' || !Array.isArray(body.roles)) {
        setRolesError(body?.detail || 'Unable to load account roles right now.');
        setRoles([]);
        setSelectedRole('');
        return;
      }

      const normalized = body.roles
        .map((r) => r?.trim())
        .filter((r): r is string => Boolean(r))
        .map((r) => r.toLowerCase());

      setRoles(normalized);
      setSelectedRole(normalized[0] ?? '');
    } catch {
      setRolesError('Unable to load account roles right now.');
      setRoles([]);
      setSelectedRole('');
    } finally {
      setIsRolesLoading(false);
    }
  }, [acc_path_name, apiFetchRequest, handleCommonAuthNav, org_path_name]);

  // open/close dialog
  const handleOpenDialog = () => {
    setDialogMessage(null);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (isAddingStaff) return;
    setIsDialogOpen(false);
    setInputValue('');
    setDebouncedQuery('');
    setSearchResults([]);
    setSearchError(null);
    setSelectedCandidate(null);
    setHasSearched(false);
    setDialogMessage(null);
    setRolesError(null);
    setRoles([]);
    setSelectedRole('');
    setIsInviting(false);
    abortRef.current?.abort();
  };

  useEffect(() => {
    if (!org_path_name || !acc_path_name) {
      navigate('/404.html');
      return;
    }
    setActionMessage(null);
    fetchAccountStaffData();
  }, [acc_path_name, fetchAccountStaffData, navigate, org_path_name]);

  useEffect(() => {
    if (!isDialogOpen) return;

    setInputValue('');
    setDebouncedQuery('');
    setSearchResults([]);
    setSearchError(null);
    setSelectedCandidate(null);
    setDialogMessage(null);
    setHasSearched(false);
    setRolesError(null);
    setSelectedRole('');
    setRoles([]);
    abortRef.current?.abort();

    void fetchAssignableRoles();
  }, [fetchAssignableRoles, isDialogOpen]);

  useEffect(() => {
    if (!canManageStaff || !isDialogOpen) return;

    const trimmed = inputValue.trim().toLowerCase();

    if (!EMAIL_REGEX.test(trimmed)) {
      setDebouncedQuery('');
      setIsSearching(false);
      setSearchError(null);
      setSearchResults([]);
      setHasSearched(false);
      abortRef.current?.abort();
      return;
    }

    const handle = window.setTimeout(() => {
      setDebouncedQuery(trimmed);
    }, 650);

    return () => window.clearTimeout(handle);
  }, [canManageStaff, inputValue, isDialogOpen]);

  useEffect(() => {
    if (!canManageStaff || !org_path_name || !acc_path_name || !isDialogOpen) return;
    if (!debouncedQuery) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    (async () => {
      setIsSearching(true);
      setSearchError(null);

      try {
        const options = {
          endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
            acc_path_name,
          )}/staff/search?query=${encodeURIComponent(debouncedQuery)}`,
          method: 'GET',
          signal: controller.signal,
        } as RequestOptions & { signal?: AbortSignal };

        const response = await apiFetchRequest(options);
        if (!response) {
          setSearchError('Unable to search right now. Please try again.');
          setSearchResults([]);
          setHasSearched(false);
          return;
        }

        const body = response.body as {
          message?: string;
          results?: StaffSearchResult[];
          detail?: string;
          status_code?: number;
        };

        if (handleCommonAuthNav(response.status, body)) return;

        if (body?.message === 'INVALID_QUERY') {
          setSearchError('Enter a valid email address to search.');
          setSearchResults([]);
          setHasSearched(false);
          return;
        }

        if (response.isError || body?.message !== 'SUCCESS' || !Array.isArray(body.results)) {
          setSearchError(body?.detail || 'Unable to find matching users.');
          setSearchResults([]);
          setHasSearched(false);
          return;
        }

        setSearchResults(body.results);
        setHasSearched(true);
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          setSearchError('Unable to search right now. Please try again.');
          setSearchResults([]);
          setHasSearched(false);
        }
      } finally {
        setIsSearching(false);
      }
    })();

    return () => controller.abort();
  }, [
    acc_path_name,
    apiFetchRequest,
    canManageStaff,
    debouncedQuery,
    handleCommonAuthNav,
    isDialogOpen,
    org_path_name,
  ]);

  const handleConfirmAddStaff = async () => {
    if (!org_path_name || !acc_path_name) return;

    if (!selectedCandidate?.user_id) {
      setDialogMessage({ type: 'error', text: 'Select a user to add to the account.' });
      return;
    }
    if (!selectedRole) {
      setDialogMessage({ type: 'error', text: 'Select a role for the new staff member.' });
      return;
    }

    setIsAddingStaff(true);
    setDialogMessage(null);

    let shouldClose = false;
    try {
      const options: RequestOptions = {
        endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(acc_path_name)}/staff/`,
        method: 'POST',
        body: { user_id: selectedCandidate.user_id, role: selectedRole },
      };

      const response = await apiFetchRequest(options);
      if (!response) {
        setDialogMessage({ type: 'error', text: 'Unable to add staff member.' });
        return;
      }

      const body = response.body as { message?: string; detail?: string; status_code?: number };
      if (handleCommonAuthNav(response.status, body)) return;

      if (response.isError || body?.message !== 'SUCCESS') {
        setDialogMessage({ type: 'error', text: body?.detail || 'Unable to add staff member.' });
        return;
      }

      setActionMessage({ type: 'success', text: 'Staff member added successfully.' });
      await fetchAccountStaffData();
      shouldClose = true;
    } catch {
      setDialogMessage({ type: 'error', text: 'Unable to add staff member. Please try again.' });
    } finally {
      setIsAddingStaff(false);
      if (shouldClose) handleCloseDialog();
    }
  };

  const handleSendInvite = async () => {
    if (!org_path_name || !acc_path_name) return;

    const trimmed = inputValue.trim();
    if (!EMAIL_REGEX.test(trimmed)) {
      setDialogMessage({ type: 'error', text: 'Enter a valid email address to send an invite.' });
      return;
    }

    setIsInviting(true);
    setDialogMessage(null);
    let shouldCloseDialog = false;

    try {
      const inviteRole = (selectedRole || 'member').toLowerCase();
      const options: RequestOptions = {
        endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
          acc_path_name,
        )}/staff/invite`,
        method: 'POST',
        body: { email: trimmed, role: inviteRole },
      };

      const response = await apiFetchRequest(options);
      if (!response) {
        setDialogMessage({ type: 'error', text: 'Unable to send invite.' });
        return;
      }

      const body = response.body as { message?: string; detail?: string; status_code?: number };
      if (handleCommonAuthNav(response.status, body)) return;

      if (response.isError || body?.message !== 'SUCCESS') {
        setDialogMessage({ type: 'error', text: body?.detail || 'Unable to send invite.' });
        return;
      }

      setActionMessage({ type: 'success', text: 'Invite sent successfully.' });
      shouldCloseDialog = true;
    } catch {
      setDialogMessage({ type: 'error', text: 'Unable to send invite right now. Please try again.' });
    } finally {
      setIsInviting(false);
      if (shouldCloseDialog) handleCloseDialog();
    }
  };

  const handleRemoveStaff = async (member: AccountStaffMember) => {
    if (!org_path_name || !acc_path_name || !member.user_id) return;
    if (!canManageStaff) return;

    setRemovingUserId(member.user_id);
    setActionMessage(null);

    try {
      const options: RequestOptions = {
        endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
          acc_path_name,
        )}/staff/${encodeURIComponent(member.user_id)}`,
        method: 'DELETE',
      };

      const response = await apiFetchRequest(options);
      if (!response) {
        setActionMessage({ type: 'error', text: 'Unable to remove staff member.' });
        return;
      }

      const body = response.body as { message?: string; detail?: string; status_code?: number };
      if (handleCommonAuthNav(response.status, body)) return;

      if (response.isError || body?.message !== 'SUCCESS') {
        const msg =
          body?.message === 'SELF_REMOVE_FORBIDDEN'
            ? 'You cannot remove yourself from account staff.'
            : body?.detail || 'Unable to remove staff member.';
        setActionMessage({ type: 'error', text: msg });
        return;
      }

      setActionMessage({ type: 'success', text: 'Staff member removed successfully.' });
      await fetchAccountStaffData();
    } catch {
      setActionMessage({ type: 'error', text: 'Unable to remove staff member. Please try again.' });
    } finally {
      setRemovingUserId(null);
    }
  };

  if ((isLoading && !account && !errorText) || !org_path_name || !acc_path_name) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', height: '100vh' }}>
        <GradientCircularProgress />
      </Box>
    );
  }

  if (errorText) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', px: 2 }}>
        <Typography color="error">{errorText}</Typography>
      </Box>
    );
  }

  if (!account) return <></>;

  const searchDropdownOpen =
    isDialogOpen && !!debouncedQuery && !isSearching && searchResults.length > 0 && !selectedCandidate;

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'transparent',
          padding: '0px 40px',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 4,
          flexWrap: 'wrap',
          mt: 0,
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Account Staff
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Manage staff access for {account.name}
          </Typography>
        </Box>
        {canManageStaff && staff.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <BorderedButton onClick={handleOpenDialog} buttonText="Add Staff" {...addStaffButtonProps} />
          </Box>
        )}
      </Box>

      {actionMessage && (
        <Box sx={{ px: '40px', mb: 4 }}>
          <Alert severity={actionMessage.type} onClose={() => setActionMessage(null)}>
            {actionMessage.text}
          </Alert>
        </Box>
      )}

      <Box
        sx={{ p: 2 }}
        style={{
          backgroundColor: theme.layout.defaults.backgroundColor,
          borderRadius: '16px',
          padding: '40px',
          width: '100%',
        }}
      >
        <Box>
          {staff.length ? (
            <StaffTable<AccountStaffMember>
              title=""
              staff={staff}
              getRowKey={(member, index) => member.acc_staff_id ?? member.user_id ?? index}
              renderActions={
                false && canManageStaff
                  ? (member) => (
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleRemoveStaff(member);
                        }}
                      >
                        <GhostButton
                          sxProps={{
                            display: 'flex',
                            padding: '2px',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '2px',
                            alignSelf: 'stretch',
                            '& .MuiButton-startIcon': {
                              height: '18px',
                              width: '18px',
                              backgroundImage: `url(${theme.images.deleteIcon.default})`,
                            },
                            '&:hover': {
                              background: 'transparent',
                              color: theme.buttons.ghost.hover.color,
                              border: '2px solid',
                              '& .MuiButton-startIcon': {
                                height: '18px',
                                width: '18px',
                                backgroundImage: `url(${theme.images.deleteIcon.hover})`,
                              },
                            },
                          }}
                          additionalProps={{
                            style: { border: 'none', height: '12px', marginRight: '0px' },
                            startIcon: <></>,
                          }}
                          disabled={!!removingUserId}
                          buttonText={removingUserId === member.user_id ? 'Removing...' : ''}
                        />
                      </Box>
                    )
                  : undefined
              }
            />
          ) : (
            <Box
              sx={{
                textAlign: 'center',
              }}
            >
              <Typography variant="h6" sx={{ mb: canManageStaff ? 2 : 0 }}>
                No staff members assigned to this account.
              </Typography>
              {canManageStaff && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <BorderedButton onClick={handleOpenDialog} buttonText="Add Staff" {...addStaffButtonProps} />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <AddStaffModal
        open={isDialogOpen}
        searchResults={searchResults}
        onClose={handleCloseDialog}
        onSearchInputChange={(value) => {
          setInputValue(value);
          setSearchError(null);
          setDialogMessage(null);
        }}
        onCandidateChange={(candidate) => {
          setSelectedCandidate(candidate ? { ...candidate, email: candidate.email ?? undefined } : null);
          setDialogMessage(null);
        }}
        onRoleChange={(event: SelectChangeEvent<string>) => {
          setSelectedRole(event.target.value);
          setDialogMessage(null);
        }}
        onAddStaff={handleConfirmAddStaff}
        onSendInvite={handleSendInvite}
        inputValue={inputValue}
        isSearching={isSearching}
        hasSearched={hasSearched}
        searchError={searchError}
        dialogMessage={dialogMessage}
        roles={roles}
        selectedRole={selectedRole}
        isRolesLoading={isRolesLoading}
        rolesError={rolesError}
        isAddingStaff={isAddingStaff}
        isInviting={isInviting}
        selectedCandidate={selectedCandidate}
        searchDropdownOpen={searchDropdownOpen}
      />
    </>
  );
}
